import { useEffect, useState } from "react";
import { subusersApi, messageFrom } from "../api";

const fmtDate = (d) => (d ? String(d).replace("T", " ").slice(0, 16) : "—");

function SubUsers() {
    const [list, setList] = useState(null);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");

    const emptyForm = { name: "", username: "", password: "", request_limit: 10 };
    const [showCreate, setShowCreate] = useState(false);
    const [form, setForm] = useState(emptyForm);

    const [pwUser, setPwUser] = useState(null);
    const [pwValue, setPwValue] = useState("");

    const [limitEdit, setLimitEdit] = useState({}); // id -> value being edited

    const flash = (msg) => { setNotice(msg); setTimeout(() => setNotice(""), 3500); };

    const load = () => {
        setError("");
        subusersApi
            .list()
            .then((res) => setList(res.data.data))
            .catch((err) => setError(messageFrom(err, "Could not load sub-users")));
    };
    useEffect(load, []);

    const submitCreate = () => {
        setError("");
        subusersApi
            .create(form)
            .then(() => {
                setShowCreate(false);
                setForm(emptyForm);
                flash("Sub-user created");
                load();
            })
            .catch((err) => setError(messageFrom(err, "Could not create sub-user")));
    };

    const saveLimit = (u) => {
        const value = limitEdit[u.id];
        if (value === undefined || String(value) === String(u.limit)) {
            setLimitEdit((s) => ({ ...s, [u.id]: undefined }));
            return;
        }
        setError("");
        subusersApi
            .setLimit(u.id, value)
            .then(() => {
                setLimitEdit((s) => ({ ...s, [u.id]: undefined }));
                flash("Limit updated");
                load();
            })
            .catch((err) => setError(messageFrom(err, "Could not update limit")));
    };

    const submitPassword = () => {
        setError("");
        subusersApi
            .setPassword(pwUser.id, pwValue)
            .then(() => {
                setPwUser(null);
                setPwValue("");
                flash("Password changed");
            })
            .catch((err) => setError(messageFrom(err, "Could not change password")));
    };

    const toggleBlock = (u) => {
        const blocking = u.active === 1;
        if (blocking && !window.confirm(`Block ${u.username}?`)) return;
        setError("");
        subusersApi
            .setActive(u.id, blocking ? 0 : 1)
            .then(() => { flash(blocking ? "Sub-user blocked" : "Sub-user unblocked"); load(); })
            .catch((err) => setError(messageFrom(err, "Could not update sub-user")));
    };

    const removeUser = (u) => {
        if (!window.confirm(`Remove ${u.username} permanently?`)) return;
        setError("");
        subusersApi
            .remove(u.id)
            .then(() => { flash("Sub-user removed"); load(); })
            .catch((err) => setError(messageFrom(err, "Could not remove sub-user")));
    };

    return (
        <section>
            <div className="admin__section-head">
                <h2>Sub-users</h2>
                <div className="admin__head-actions">
                    <button className="btn btn--ghost" onClick={load}>Refresh</button>
                    <button className="btn btn--primary" onClick={() => setShowCreate(true)}>+ New sub-user</button>
                </div>
            </div>

            <p className="subusers__hint">
                Sub-users pull reports from <b>your</b> report balance. Their personal limit caps how much of
                your balance they can spend — you can change it anytime.
            </p>

            {error && <div className="banner banner--error">{error}</div>}
            {notice && <div className="banner banner--ok">{notice}</div>}

            {list === null ? (
                <div className="admin__empty">Loading…</div>
            ) : list.length === 0 ? (
                <div className="admin__empty">No sub-users yet — create one to share your report balance.</div>
            ) : (
                <div className="admin__table-wrap">
                    <table className="admin__table">
                        <thead>
                            <tr>
                                <th>Name</th><th>Username</th><th>Status</th>
                                <th>Limit</th><th>Used</th><th>Remaining</th>
                                <th>Last report</th><th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {list.map((u) => (
                                <tr key={u.id}>
                                    <td>{u.name || "—"}</td>
                                    <td>{u.username}</td>
                                    <td>
                                        <span className={`admin__pill ${u.active ? "admin__pill--ok" : "admin__pill--err"}`}>
                                            {u.active ? "active" : "blocked"}
                                        </span>
                                    </td>
                                    <td>
                                        {limitEdit[u.id] !== undefined ? (
                                            <span className="subusers__limit-edit">
                                                <input
                                                    type="number" min="0" autoFocus
                                                    value={limitEdit[u.id]}
                                                    onChange={(e) => setLimitEdit((s) => ({ ...s, [u.id]: e.target.value }))}
                                                    onKeyDown={(e) => { if (e.key === "Enter") saveLimit(u); }}
                                                />
                                                <button className="btn btn--primary btn--xs" onClick={() => saveLimit(u)}>Save</button>
                                            </span>
                                        ) : (
                                            <button
                                                className="subusers__limit"
                                                title="Click to change"
                                                onClick={() => setLimitEdit((s) => ({ ...s, [u.id]: u.limit }))}
                                            >
                                                {u.limit} ✎
                                            </button>
                                        )}
                                    </td>
                                    <td>{u.used}</td>
                                    <td>{u.remaining}</td>
                                    <td>{fmtDate(u.last_report_at)}</td>
                                    <td>
                                        <div className="admin__row-actions">
                                            <button className="btn btn--ghost btn--xs" onClick={() => { setPwUser(u); setPwValue(""); }}>Password</button>
                                            <button className="btn btn--ghost btn--xs" onClick={() => toggleBlock(u)}>
                                                {u.active ? "Block" : "Unblock"}
                                            </button>
                                            <button className="btn btn--ghost btn--xs btn--danger" onClick={() => removeUser(u)}>Remove</button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {showCreate && (
                <div className="admin__overlay" onClick={() => setShowCreate(false)}>
                    <div className="admin__modal" onClick={(e) => e.stopPropagation()}>
                        <h3>New sub-user</h3>
                        <label className="admin__field">
                            <span>Name</span>
                            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                        </label>
                        <label className="admin__field">
                            <span>Username *</span>
                            <input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
                        </label>
                        <label className="admin__field">
                            <span>Password * (min 6)</span>
                            <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                        </label>
                        <label className="admin__field">
                            <span>Report limit (from your balance)</span>
                            <input type="number" min="0" value={form.request_limit}
                                onChange={(e) => setForm({ ...form, request_limit: e.target.value })} />
                        </label>
                        <div className="admin__modal-actions">
                            <button className="btn btn--ghost" onClick={() => setShowCreate(false)}>Cancel</button>
                            <button className="btn btn--primary" disabled={!form.username || form.password.length < 6} onClick={submitCreate}>Create</button>
                        </div>
                    </div>
                </div>
            )}

            {pwUser && (
                <div className="admin__overlay" onClick={() => setPwUser(null)}>
                    <div className="admin__modal" onClick={(e) => e.stopPropagation()}>
                        <h3>Change password — {pwUser.username}</h3>
                        <label className="admin__field">
                            <span>New password (min 6)</span>
                            <input type="password" autoFocus value={pwValue} onChange={(e) => setPwValue(e.target.value)} />
                        </label>
                        <div className="admin__modal-actions">
                            <button className="btn btn--ghost" onClick={() => setPwUser(null)}>Cancel</button>
                            <button className="btn btn--primary" disabled={pwValue.length < 6} onClick={submitPassword}>Save</button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}

export default SubUsers;
